<template>
  <div class="table-pagination">
    <level>
      <template v-if="shouldShowSimple" class="flex">
        <jb-buttons>
          <jb-button v-for="page in allPages" :key="`simple-${page}`" @click="$emit('page-changed', page)"
                     :active="page === currentPage" :label="page + 1" small />
        </jb-buttons>
      </template>

      <template v-else>
        <jb-buttons>
          <jb-button v-for="page in firstPages" :key="`first-${page}`" @click="$emit('page-changed', page)"
                     :active="page === currentPage" :label="page + 1" small />

          <span v-if="showFirstSeparator" class="pagination-separator">...</span>

          <jb-button v-for="page in middlePages" :key="`middle-${page}`" @click="$emit('page-changed', page)"
                     :active="page === currentPage" :label="page + 1" small />

          <span v-if="showLastSeparator" class="pagination-separator">...</span>

          <jb-button v-for="page in lastPages" :key="`last-${page}`" @click="$emit('page-changed', page)"
                     :active="page === currentPage" :label="page + 1" small />
        </jb-buttons>
      </template>
      <small>{{ __('Page') }} {{ currentPageHuman }} {{ __('of') }} {{ totalPages }}</small>
    </level>
  </div>
</template>

<script>
import { computed } from 'vue'
import Level from '@/Components/Dashboard/Level'
import JbButtons from '@/Components/Dashboard/JbButtons.vue'
import JbButton from '@/Components/Dashboard/JbButton.vue'
import __ from "@/trans"

export default {
  name: 'Pagination',
  components: {
    Level,
    JbButtons,
    JbButton
  },
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    }
  },
  emits: ['page-changed'],
  setup(props) {
    const currentPageHuman = computed(() => props.currentPage + 1)

    const shouldShowSimple = computed(() => props.totalPages <= 7)

    const allPages = computed(() => {
      if (!shouldShowSimple.value) return []

      const pages = []
      for (let i = 0; i < props.totalPages; i++) {
        pages.push(i)
      }
      return pages
    })

    const firstPages = computed(() => {
      if (shouldShowSimple.value) return []

      const pages = []
      for (let i = 0; i < Math.min(3, props.totalPages); i++) {
        pages.push(i)
      }
      return pages
    })

    const lastPages = computed(() => {
      if (shouldShowSimple.value) return []

      const pages = []
      const startPage = Math.max(props.totalPages - 3, 0)

      for (let i = startPage; i < props.totalPages; i++) {
        pages.push(i)
      }
      return pages
    })

    const middlePages = computed(() => {
      if (shouldShowSimple.value) return []

      const pages = []
      const firstPagesSet = new Set(firstPages.value)
      const lastPagesSet = new Set(lastPages.value)

      const start = Math.max(0, props.currentPage - 3)
      const end = Math.min(props.totalPages - 1, props.currentPage + 3)

      for (let i = start; i <= end; i++) {
        if (!firstPagesSet.has(i) && !lastPagesSet.has(i)) {
          pages.push(i)
        }
      }

      return pages
    })

    const showFirstSeparator = computed(() => {
      if (shouldShowSimple.value || firstPages.value.length === 0 || middlePages.value.length === 0) {
        return false
      }

      const lastFirstPage = firstPages.value[firstPages.value.length - 1]
      const firstMiddlePage = middlePages.value[0]

      return firstMiddlePage - lastFirstPage > 1
    })

    const showLastSeparator = computed(() => {
      if (shouldShowSimple.value || lastPages.value.length === 0 || middlePages.value.length === 0) {
        return false
      }

      const lastMiddlePage = middlePages.value[middlePages.value.length - 1]
      const firstLastPage = lastPages.value[0]

      return firstLastPage - lastMiddlePage > 1
    })

    return {
      currentPageHuman,
      shouldShowSimple,
      allPages,
      firstPages,
      lastPages,
      middlePages,
      showFirstSeparator,
      showLastSeparator
    }
  }
}
</script>

<style scoped>
.pagination-separator {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  color: #6b7280;
  font-size: 14px;
}
</style>
